import { DataSource } from "@angular/cdk/collections";
import { BehaviorSubject, merge, Observable, Subject } from "rxjs";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { map, startWith, takeUntil, tap } from "rxjs/operators";
import { PrescriptionService } from "@core/http/table-data-services/prescription.service";
import { Prescription } from "@core/models/Prescription";
import { FilterRecetaPatient } from "@core/models/FilterRecetaPatient";

export class PrescriptionbyIdPatientDataSource extends DataSource<Prescription> {
  filterChange = new BehaviorSubject<string>('');
  filteredData: Prescription[] = [];
  renderedData: Prescription[] = [];

  // ✅ trigger explícito para volver a pedir al backend
  private readonly reload$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    public exampleDatabase: PrescriptionService,
    public paginator: MatPaginator,
    public _sort: MatSort,
    public filterReceta: FilterRecetaPatient
  ) {
    super();

    // cada vez que cambia el filtro del buscador, regresamos a la página 0
    this.filterChange.subscribe(() => (this.paginator.pageIndex = 0));
  }

  /** ✅ Llama esto desde el componente cuando cambie idPaciente / fechas */
  reload(filterReceta?: FilterRecetaPatient): void {
    if (filterReceta) this.filterReceta = filterReceta;
    if (this.paginator) this.paginator.pageIndex = 0;

    // dispara el flujo y, por ende, el HTTP
    this.reload$.next();
  }

  get filter(): string {
    return this.filterChange.value;
  }

  set filter(filter: string) {
    this.filterChange.next(filter);
  }

  /** Connect: stream que alimenta la tabla */
  connect(): Observable<Prescription[]> {
    const displayDataChanges = [
      this.exampleDatabase.dataChange,
      this._sort.sortChange,
      this.filterChange,
      this.paginator.page,
      this.reload$, // ✅ ahora la tabla también reacciona a reload()
    ];

    // ✅ startWith(null) hace que al conectar SIEMPRE dispare una primera carga
    return merge(...displayDataChanges).pipe(
      startWith(null),
      map(() => {

        this.filteredData = this.exampleDatabase.data
          .slice()
          .filter((prescription: Prescription) => {
            const searchStr = (
              (prescription.idReceta ?? '') +
              (prescription.idMedico ?? '') +
              (prescription.nombreMedico ?? '') +
              (prescription.idPaciente ?? '') +
              (prescription.nombrePaciente ?? '') +
              (prescription.idSucursal ?? '') +
              (prescription.idGEMP ?? '') +
              (prescription.fechaCreacion ?? '') +
              (prescription.fechaUltimaModificacion ?? '')
            ).toLowerCase();

            return searchStr.indexOf((this.filter ?? '').toLowerCase()) !== -1;
          });

        const sortedData = this.sortData(this.filteredData.slice());
        const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
        this.renderedData = sortedData.splice(startIndex, this.paginator.pageSize);

        return this.renderedData;
      }),
      takeUntil(this.destroy$)
    );
  }

  disconnect(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** sorting */
  sortData(data: Prescription[]): Prescription[] {
    if (!this._sort.active || this._sort.direction === '') {
      return data;
    }

    return data.sort((a, b) => {
      let propertyA: number | string = '';
      let propertyB: number | string = '';

      switch (this._sort.active) {
        case 'medico':
          [propertyA, propertyB] = [a.nombreMedico, b.nombreMedico];
          break;
        case 'paciente':
          [propertyA, propertyB] = [a.nombrePaciente, b.nombrePaciente];
          break;
        case 'creacion':
          [propertyA, propertyB] = [a.fechaCreacion, b.fechaCreacion];
          break;
      }

      const valueA = isNaN(+propertyA) ? propertyA : +propertyA;
      const valueB = isNaN(+propertyB) ? propertyB : +propertyB;

      return (valueA < valueB ? -1 : 1) * (this._sort.direction === 'asc' ? 1 : -1);
    });
  }
}
